package uz.ferro.erp.feature.auth

import js.objects.jso
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import mui.material.Button
import mui.material.ButtonVariant
import mui.material.CircularProgress
import mui.material.Dialog
import mui.material.DialogContent
import mui.material.DialogTitle
import mui.material.FormControlVariant
import mui.material.TextField
import mui.system.sx
import react.FC
import react.Props
import react.ReactNode
import react.dom.events.ChangeEvent
import react.dom.onChange
import react.useContext
import react.useEffect
import react.useState
import uz.ferro.erp.error.HttpException
import uz.ferro.erp.feature.alert.AlertManagerContext
import uz.ferro.erp.feature.auth.model.RegisterBody
import uz.ferro.erp.manager.AuthManager
import uz.ferro.erp.ui.AppText
import uz.ferro.erp.ui.HorizontalLayout
import uz.ferro.erp.ui.VerticalLayout
import web.cssom.AlignItems
import web.cssom.JustifyContent
import web.cssom.pct
import web.cssom.px
import web.html.HTMLInputElement
import web.html.InputType

external interface LoginDialogProps : Props {
    var isOpen: Boolean
    var onClose: () -> Unit
    var onLoginSuccess: () -> Unit
}

private enum class UiMode {
    EMAIL,
    REGISTER,
    CODE
}

val LoginDialog = FC<LoginDialogProps> { props ->

    var mode by useState(UiMode.EMAIL)
    var isProgress: Boolean by useState(false)
    var emailInput: String by useState("")
    var codeInput: String by useState("")
    var firstNameInput: String by useState("")
    var lastNameInput: String by useState("")
    var isValidEmail: Boolean by useState(false)
    var isValidCode: Boolean by useState(false)
    var alertManager = useContext(AlertManagerContext)

    var isEmailMode by useState(mode == UiMode.EMAIL)
    var isRegisterMode by useState(mode == UiMode.REGISTER)
    var isCodeMode by useState(mode == UiMode.CODE)

    fun close() {
        mode = UiMode.EMAIL
        isValidEmail = false
        isValidCode = false
        emailInput = ""
        codeInput = ""
        firstNameInput = ""
        lastNameInput = ""
        isProgress = false
        props.onClose()
    }

    fun authInServer(jwt: String) {
        MainScope().launch {
            isProgress = true
            try {
                val result = AuthManager.authWithGoogle(jwt)
                if (result.token.orEmpty().isNotBlank()) {
                    props.onLoginSuccess()
                    close()
                }
            } catch (e: Exception) {
            }
            isProgress = false
        }
    }

    useEffect(emailInput) {
        val email = emailInput.trim()
        val indexOfSeparator = email.indexOf("@")
        isValidEmail = indexOfSeparator in (1..<email.lastIndex) &&
                email.substring(indexOfSeparator + 1).contains(".")
    }

    useEffect(codeInput) {
        isValidCode = codeInput.isNotBlank()
    }

    useEffect(mode) {
        isEmailMode = mode == UiMode.EMAIL
        isRegisterMode = mode == UiMode.REGISTER
        isCodeMode = mode == UiMode.CODE
    }

    Dialog {
        open = props.isOpen
        fullWidth = true
        onClose = { _, _ ->
            close()
        }

        DialogTitle {
            +"Tizimga kirish"
        }

        DialogContent {
            HorizontalLayout {
                sx {
                    justifyContent = JustifyContent.center
                }
                VerticalLayout {
                    space = 16.px
                    sx {
                        width = 100.pct
                        maxWidth = 400.px
                        alignItems = AlignItems.center
                        justifyContent = JustifyContent.center
                        padding = 16.px
                    }

                    TextField {
                        sx {
                            width = 100.pct
                        }
                        disabled = isEmailMode.not()
                        label = ReactNode("Email")
                        InputLabelProps = jso {
                            shrink = true
                        }
                        variant = FormControlVariant.outlined
                        onChange = { event ->
                            event as ChangeEvent<HTMLInputElement>
                            val email = event.target.value
                            emailInput = email
                        }
                    }

                    if (isRegisterMode) {
                        TextField {
                            sx {
                                width = 100.pct
                            }
                            label = ReactNode("Ismingiz")
                            InputLabelProps = jso {
                                shrink = true
                            }
                            variant = FormControlVariant.outlined
                            onChange = { event ->
                                event as ChangeEvent<HTMLInputElement>
                                firstNameInput = event.target.value
                            }
                        }

                        TextField {
                            sx {
                                width = 100.pct
                            }
                            label = ReactNode("Familiyangiz")
                            InputLabelProps = jso {
                                shrink = true
                            }
                            variant = FormControlVariant.outlined
                            onChange = { event ->
                                event as ChangeEvent<HTMLInputElement>
                                lastNameInput = event.target.value
                            }
                        }
                    }

                    if (isEmailMode) {
                        Button {
                            disabled = isProgress || isValidEmail.not()
                            sx {
                                width = 100.pct
                            }

                            variant = ButtonVariant.contained

                            onClick = {
                                MainScope().launch {
                                    isProgress = true
                                    try {
                                        AuthManager.authWithEmail(emailInput)
                                        mode = UiMode.CODE
                                    } catch (e: Exception) {
                                        if (e is HttpException && e.code == 404) {
                                            mode = UiMode.REGISTER
                                        } else {
                                            alertManager.handleError(e)
                                        }
                                    }
                                    isProgress = false
                                }
                            }

                            +"Davom etish"
                        }
                    }

                    if (isRegisterMode) {
                        Button {
                            disabled = isProgress || isValidEmail.not() ||
                                    firstNameInput.isBlank() || lastNameInput.isBlank()
                            sx {
                                width = 100.pct
                            }

                            variant = ButtonVariant.contained

                            onClick = {
                                MainScope().launch {
                                    isProgress = true
                                    try {
                                        AuthManager.register(
                                            RegisterBody(
                                                email = emailInput,
                                                firstName = firstNameInput,
                                                lastName = lastNameInput,
                                                patronymic = null
                                            )
                                        )
                                        mode = UiMode.CODE
                                    } catch (e: Exception) {
                                        alertManager.handleError(e)
                                    }
                                    isProgress = false
                                }
                            }

                            +"Davom etish"
                        }
                    }

                    if (isCodeMode) {
                        TextField {
                            sx {
                                width = 100.pct
                            }
                            label = ReactNode("Kod")
                            type = InputType.number
                            InputLabelProps = jso {
                                shrink = true
                            }
                            variant = FormControlVariant.outlined
                            onChange = { event ->
                                event as ChangeEvent<HTMLInputElement>
                                val code = event.target.value
                                codeInput = code
                            }
                        }

                        Button {
                            disabled = isProgress && isValidCode.not()
                            sx {
                                width = 100.pct
                            }
                            variant = ButtonVariant.contained

                            onClick = {
                                MainScope().launch {
                                    isProgress = true
                                    try {
                                        val result = AuthManager.confirmEmail(
                                            email = emailInput,
                                            code = codeInput
                                        )
                                        if (result.token.orEmpty().isNotBlank()) {
                                            props.onLoginSuccess()
                                            close()
                                        }
                                    } catch (e: Exception) {
                                    }
                                    isProgress = false
                                }
                            }

                            +"Tasdiqlash"
                        }
                    }

                    if (isProgress) {
                        CircularProgress()
                    }

                    if (isProgress.not() && isEmailMode) {
                        HorizontalLayout {
                            sx {
                                justifyContent = JustifyContent.center
                            }

                            AppText {
                                text = "yoki"
                            }
                        }

                        GoogleAuthButton {
                            onSuccess = ::authInServer
                        }
                    }
                }
            }
        }
    }
}